<template>
	<el-config-provider :locale="locale" :size="size">
		<div class="app-wrapper">
			<router-view v-slot="{ Component }">
				<keep-alive :include="cachedViews">
					<component :is="Component" />
				</keep-alive>
			</router-view>
			<!-- <performance-monitor v-if="showPerformanceMonitor" /> -->
			<component :is="AiAssistantComponent" v-if="isAiAssistantVisible" />
			<!-- <div v-if="showScaleTip" class="scale-tip-overlay">
				<div class="scale-tip-content">
					<h3>屏幕尺寸提示</h3>
					<p>您正在使用小屏幕设备，为获得最佳显示效果，建议将浏览器缩放比例调整为 {{ recommendedScale }}%</p>
					<p class="scale-tip-current">当前缩放比例：{{ currentScale }}%</p>
					<el-button type="primary" @click="closeScaleTip">我知道了</el-button>
				</div>
			</div> -->
		</div>
	</el-config-provider>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, onBeforeUnmount, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useTitle } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { messages } from '@/i18n'
import { handleThemeStyle } from '@/utils/theme'
import { useAppStore } from '@/store/modules/app'
import { useUserStore } from '@/store/modules/user'
import PerformanceMonitor from '@/components/PerformanceMonitor.vue'
import AiAssistant from '@/components/AiAssistant/AiAssistant.vue'
import AiAssistantQilu from '@/components/AiAssistant/AiAssistantQilu.vue'
const AiAssistantComponent = import.meta.env.VITE_PROJECT === 'qilu' ? AiAssistantQilu : AiAssistant
import { ElMessage } from 'element-plus'
import { getAppConfig } from '@/utils/appConfig'
import { aiAssistantEnabled } from '@/utils/aiAssistant'
// 暂时注释掉水印功能
// import { createDynamicWatermark } from '@/utils/watermark'
const userStore = useUserStore()
const appStore = useAppStore()
const route = useRoute()
const { t } = useI18n()
const locale = computed(() => messages[appStore.language].el)
const size = computed(() => appStore.componentSize)

// 从配置文件获取设置
const appConfig = getAppConfig()
const showPerformanceMonitor = ref(import.meta.env.MODE == 'development' || (appConfig?.features?.performanceMonitor ?? false))

const cachedViews = ref<string[]>([])
const isAiAssistantVisible = computed(() => {
	// 如果当前路由是登录页面，则不显示AI助手
	if (route.path === '/login') {
		return false
	}
	return userStore.user?.id && (userStore.user?.aiAssistantStatus ?? true) && aiAssistantEnabled.value
})
const addCachedView = (name: string) => {
	if (!cachedViews.value.includes(name)) {
		cachedViews.value.push(name)
	}
}

const removeCachedView = (name: string) => {
	const index = cachedViews.value.indexOf(name)
	if (index > -1) {
		cachedViews.value.splice(index, 1)
	}
}

const showScaleTip = ref(false)
const currentScale = ref(100)
const recommendedScale = ref(65)
const screenMinWidth = 1440 //

const checkScreenScale = () => {
	const zoom = detectZoom()

	currentScale.value = parseInt(String(zoom * 100))

	const physicalWidth = window.screen.width
	const isSmallScreen = physicalWidth < screenMinWidth

	if (isSmallScreen) {
		recommendedScale.value = 67

		if (!localStorage.getItem('scale-tip-closed')) {
			showScaleTip.value = true
			console.log('显示缩放提示')
		} else {
			console.log('用户已关闭提示，不再显示', physicalWidth)
		}
	}
}

const detectZoom = () => {
	let ratio = 0

	if (window.outerWidth && window.innerWidth && window.innerWidth > 0) {
		ratio = window.outerWidth / window.innerWidth
	} else if (window.devicePixelRatio !== undefined) {
		ratio = window.devicePixelRatio
	} else {
		ratio = 1
	}

	return ratio
}

const closeScaleTip = () => {
	showScaleTip.value = false
	localStorage.setItem('scale-tip-closed', 'true')
}

const resetScaleTip = () => {
	localStorage.removeItem('scale-tip-closed')
	checkScreenScale()
}

let resizeTimer: number | null = null
const handleResize = () => {
	if (resizeTimer) {
		clearTimeout(resizeTimer)
	}
	resizeTimer = window.setTimeout(() => {
		const zoom = detectZoom()
		currentScale.value = parseInt(String(zoom * 100))
	}, 300)
}

// 动态更新 --base-card-height CSS 变量
const updateBaseCardHeight = () => {
	const root = document.documentElement
	if (appStore.theme.isLayoutView) {
		root.style.setProperty('--base-card-height', 'calc(100vh - 82px - var(--theme-header-height))')
	} else {
		root.style.setProperty('--base-card-height', 'calc(100vh - 36px)')
	}
}

// 监听 isLayoutView 的变化
watch(
	() => appStore.theme.isLayoutView,
	newValue => {
		updateBaseCardHeight()
	},
	{ immediate: true }
)

watch(
	() => [userStore.token, userStore.user?.username],
	([token, username]) => {
		// 暂时注释掉水印功能
		// if (window.location.origin.includes('test')) {
		// 	if (token && username) {
		// 		createDynamicWatermark(`演示环境 - ${username}`)
		// 	} else {
		// 		createDynamicWatermark('演示环境')
		// 	}
		// }
	},
	{ deep: true }
)

// // 设置标题 - 优先使用配置文件中的标题
// const pageTitle = appConfig?.app?.title || t('app.title')
// useTitle(pageTitle)

onMounted(() => {
	if ('serviceWorker' in navigator) {
		// 卸载所有 Service Worker
		navigator.serviceWorker.getRegistrations().then(registrations => {
			registrations.forEach(registration => {
				registration.unregister()
				console.log('Service Worker unregistered:', registration.scope)
			})
		})
	}
	nextTick(() => {
		handleThemeStyle(appStore.theme)
		updateBaseCardHeight()

		checkScreenScale()
		// 暂时注释掉水印功能
		// if (window.location.origin.includes('notest')) {
		// 	if (userStore.token && userStore.user?.username) {
		// 		// 如果已登录且有用户信息，创建带用户名的水印
		// 		createDynamicWatermark(`演示环境 - ${userStore.user.username}`)
		// 	} else {
		// 		// 否则创建基础水印
		// 		createDynamicWatermark('演示环境')
		// 	}
		// }

		window.addEventListener('resize', handleResize)
	})

	// 监听自定义事件，用于其他组件控制缓存
	window.addEventListener('addCachedView', (e: any) => {
		if (e.detail) {
			addCachedView(e.detail)
		}
	})

	window.addEventListener('removeCachedView', (e: any) => {
		if (e.detail) {
			removeCachedView(e.detail)
		}
	})
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize)
	if (resizeTimer) {
		clearTimeout(resizeTimer)
	}
	window.removeEventListener('addCachedView', (e: any) => {})
	window.removeEventListener('removeCachedView', (e: any) => {})
})
</script>

<style lang="scss">
.app-wrapper {
	position: relative;
	width: 100%;
	height: 100%;
}

.scale-tip-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.7);
	z-index: 9999;
	display: flex;
	justify-content: center;
	align-items: center;

	.scale-tip-content {
		background-color: #fff;
		border-radius: 8px;
		padding: 20px 30px;
		max-width: 90%;
		width: 420px;
		text-align: center;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

		h3 {
			font-size: 18px;
			margin-bottom: 15px;
			color: #333;
		}

		p {
			font-size: 14px;
			line-height: 1.6;
			margin-bottom: 10px;
			color: #666;
		}

		.scale-tip-current {
			color: #409eff;
			font-weight: bold;
			margin-bottom: 20px;
		}

		button {
			margin-top: 5px;
		}
	}
}
</style>
